// features/workstations/dashboard-sections/macro-gate.js
// ═══════════════════════════════════════════════════════════
// Macro Gate (Long-Term 10x)
// ═══════════════════════════════════════════════════════════
// "Are we cleared to add new Pilot/Conviction positions?"
// Tied to the EHIQ stress reading in the hard_rules:
//   - stress ≤ 30 → DEPLOY (green)
//   - stress 31-50 → HOLD (amber) — existing positions only
//   - stress > 50 → PROTECT (red) — no new positions
//
// V1: reads the macro gate rule from config. The stress value
// displays as "awaiting EHIQ feed" until Phase 3b wires it.
// V2: live stress + historical chart + auto-flip to PROTECT.
// ═══════════════════════════════════════════════════════════

import ShiftRead from './shift-read';

export default function MacroGateSection({ workstation }) {
  const rules = workstation?.config?.criteria?.hard_rules || [];
  const macroRule = rules.find((r) => r.id === 'macro_gate');

  // Static until Phase 3b; acts as a reserved slot.
  const currentState = 'awaiting';

  const stateMeta = {
    deploy:   { label: 'Deploy',  tone: 'sage',  note: 'New positions cleared' },
    hold:     { label: 'Hold',    tone: 'gold',  note: 'Existing only · no new entries' },
    protect:  { label: 'Protect', tone: 'terra', note: 'All new entries blocked' },
    awaiting: { label: 'Awaiting feed', tone: 'dim', note: 'EHIQ wiring Phase 3b' },
  };
  const meta = stateMeta[currentState];

  return (
    <section className="ws-section ws-section-macro-gate">
      <div className="ws-section-header">
        <div className="ws-section-label">Macro Gate</div>
        <div className={`ws-gate-pill ws-gate-pill-${meta.tone}`}>
          {meta.label}
        </div>
      </div>
      <div className="ws-section-body">
        <div className="ws-gate-meters">
          <div className="ws-gate-meter">
            <div className="ws-gate-meter-label">EHIQ stress</div>
            <div className="ws-gate-meter-value">—</div>
            <div className="ws-gate-meter-note">{meta.note}</div>
          </div>

          <div className="ws-gate-bands">
            <div className="ws-gate-band ws-gate-band-sage">
              <span className="ws-gate-band-range">0 – 30</span>
              <span className="ws-gate-band-label">Deploy</span>
            </div>
            <div className="ws-gate-band ws-gate-band-gold">
              <span className="ws-gate-band-range">31 – 50</span>
              <span className="ws-gate-band-label">Hold</span>
            </div>
            <div className="ws-gate-band ws-gate-band-terra">
              <span className="ws-gate-band-range">51 – 100</span>
              <span className="ws-gate-band-label">Protect</span>
            </div>
          </div>
        </div>

        {macroRule && (
          <div className="ws-gate-rule">
            <div className="ws-gate-rule-label">Gate rule</div>
            <div className="ws-gate-rule-text">{macroRule.text}</div>
          </div>
        )}

        <ShiftRead>
          The macro gate is not a suggestion. When stress climbs,
          the best move is to do nothing — not to hunt harder for
          the &ldquo;right&rdquo; buy.
        </ShiftRead>
      </div>
    </section>
  );
}
